import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { BookCheck, History, Landmark, Layers3, ListChecks, Scale, Search } from 'lucide-react'
import {
  Button,
  Card,
  Chip,
  DetailHero,
  Drawer,
  EmptyState,
  ErrorState,
  FilterToolbar,
  LoadMore,
  MoneyValue,
  SelectField,
  SegmentedControl,
  Skeleton,
  StatusBadge,
  TechnicalDetails,
  TextField,
} from '@shared/design-system/ui'
import {
  useAccountingOverview,
  useAccountingTransaction,
  useAccountingTransactionsInfinite,
  useAccountingTrialBalance,
} from '../api/admin.api'
import type { AccountingBalance, AccountingPosting, AccountingTransaction } from '../api/admin.api'
import { useDebounce } from '@shared/lib/use-debounce'
import { TechnicalId } from './TechnicalId'
import {
  clickableRow,
  accountingAmountCell,
  accountingCell,
  accountingCellValue,
  accountingFilterGrid,
  accountingFilterSearch,
  accountingMetricHint,
  accountingPostingsTable,
  accountingPostingsHead,
  accountingPostingsBody,
  accountingPostingCell,
  accountingPostingRow,
  accountingPostingRefsCell,
  accountingPostingSideCell,
  accountingPostingValueCell,
  accountingPrimaryCell,
  accountingSkeleton,
  accountingSkeletonRow,
  accountingTable,
  accountingTableBody,
  accountingTableHead,
  accountingTableRow,
  accountingToolbar,
  accountingToolbarHeader,
  accountingViewTabs,
  fieldLabel,
  header,
  kpiValue,
  ledgerDetail,
  ledgerDetailLabel,
  ledgerDetailRow,
  ledgerDetailRows,
  ledgerDetailValue,
  ledgerStatusGroup,
  ledgerTechnicalRefs,
  metricCard,
  metricHeader,
  metricIcon,
  metricLabel,
  muted,
  jsonBox,
  page,
  statGrid,
  subtitle,
  table,
  tableCard,
  tableViewport,
  td,
  th,
  title,
  titleBlock,
} from './admin.css'

type AccountingView = 'transactions' | 'trial-balance'

const viewOptions: Array<{ value: AccountingView; label: string }> = [
  { value: 'transactions', label: 'Transações' },
  { value: 'trial-balance', label: 'Balancete' },
]

const sourceOptions = [
  { value: '', label: 'Todos os fatos' },
  { value: 'SALE_ALLOCATION', label: 'Venda alocada' },
  { value: 'SALE_REVERSAL', label: 'Venda revertida' },
  { value: 'DEBT_CREATED', label: 'Dívida criada' },
  { value: 'DEBT_SETTLEMENT', label: 'Dívida liquidada' },
  { value: 'DISPUTE_DEBT_REVERSED', label: 'Dívida revertida por disputa ganha' },
  { value: 'WITHDRAWAL_CONSUMED', label: 'Saldo reservado para recebimento' },
  { value: 'WITHDRAWAL_COMPLETED', label: 'Recebimento concluído' },
  { value: 'WITHDRAWAL_COMPLETED_SOURCE', label: 'Conversão do recebimento na origem' },
  { value: 'WITHDRAWAL_COMPLETED_DESTINATION', label: 'Conversão do recebimento no destino' },
  { value: 'WITHDRAWAL_REVERTED', label: 'Recebimento revertido' },
]

export function AdminAccountingScreen() {
  const queryClient = useQueryClient()
  const [view, setView] = useState<AccountingView>('transactions')
  const [query, setQuery] = useState('')
  const [sourceType, setSourceType] = useState('')
  const currency = 'BRL'
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const debouncedQuery = useDebounce(query.trim(), 300)
  const debouncedCurrency = useDebounce(currency.trim(), 300)
  const overview = useAccountingOverview()
  const structuralInvariantsHealthy =
    overview.data?.legacyLedgerRemoved === true &&
    overview.data.appendOnly === true &&
    overview.data.subledgerAppendOnly === true
  const overviewInvariantsHealthy = structuralInvariantsHealthy && overview.data?.unbalanced === 0
  const overviewVerificationFailed = overview.isError || overview.isRefetchError
  const accountingOverview =
    overviewInvariantsHealthy && !overviewVerificationFailed ? overview.data : undefined
  const accountingReady = accountingOverview !== undefined

  useEffect(() => {
    if (accountingReady) return

    const accountingQueryKeys = [
      ['admin-accounting-transactions'],
      ['admin-accounting-trial-balance'],
      ['admin-accounting-transaction'],
    ] as const
    const cancellations = []
    for (const queryKey of accountingQueryKeys) {
      cancellations.push(queryClient.cancelQueries({ queryKey }))
      queryClient.removeQueries({ queryKey })
    }
    void Promise.all(cancellations).then(() => setSelectedId(null))
  }, [accountingReady, queryClient])

  const transactions = useAccountingTransactionsInfinite(
    {
      ...(debouncedQuery ? { q: debouncedQuery } : {}),
      ...(sourceType ? { sourceType } : {}),
      ...(debouncedCurrency ? { currency: debouncedCurrency } : {}),
    },
    accountingReady && view === 'transactions',
  )
  const trialBalance = useAccountingTrialBalance(
    { ...(debouncedCurrency ? { currency: debouncedCurrency } : {}) },
    accountingReady && view === 'trial-balance',
  )
  const transactionItems = transactions.data?.pages.flatMap((item) => item.items) ?? []
  const trialBalanceItems = trialBalance.data?.pages.flatMap((item) => item.items) ?? []
  const hasActiveFilters = Boolean(view === 'transactions' && (query || sourceType))

  function clearFilters() {
    setQuery('')
    setSourceType('')
  }

  function handleViewChange(nextView: AccountingView) {
    if (nextView === view) return

    const inactiveQueryKey =
      nextView === 'transactions'
        ? ['admin-accounting-trial-balance']
        : ['admin-accounting-transactions']
    void queryClient.cancelQueries({ queryKey: inactiveQueryKey })
    void queryClient.cancelQueries({ queryKey: ['admin-accounting-transaction'] })
    setSelectedId(null)
    setView(nextView)
  }

  function handleTransactionSelect(transactionId: string) {
    if (selectedId && selectedId !== transactionId) {
      void queryClient.cancelQueries({ queryKey: ['admin-accounting-transaction'] })
    }
    setSelectedId(transactionId)
  }

  function handleDrawerClose() {
    void queryClient.cancelQueries({ queryKey: ['admin-accounting-transaction'] })
    setSelectedId(null)
  }

  return (
    <section className={page} data-testid="accounting-screen">
      <div className={header}>
        <div className={titleBlock}>
          <h1 className={title}>Contabilidade</h1>
          <p className={subtitle}>
            Diário imutável, partidas dobradas e rastreabilidade dos fatos financeiros.
          </p>
        </div>
        <div className={ledgerStatusGroup} aria-label="Integridade do motor financeiro">
          <StatusBadge
            label={accountingReady ? 'Diário imutável' : 'Diário não validado'}
            tone={accountingReady ? 'success' : overview.isLoading ? 'awaiting' : 'closed'}
          />
          <StatusBadge
            label={accountingReady ? 'Subledger imutável' : 'Subledger não validado'}
            tone={accountingReady ? 'success' : overview.isLoading ? 'awaiting' : 'closed'}
          />
          {accountingReady ? <StatusBadge label="Ledger legado removido" tone="success" /> : null}
        </div>
      </div>

      {overview.isLoading ? <Skeleton height={132} /> : null}
      {overview.isError && !overview.data ? (
        <ErrorState title="Não foi possível carregar a visão contábil." />
      ) : null}
      {overview.data && (!overviewInvariantsHealthy || overview.isRefetchError) ? (
        <ErrorState
          title={
            !structuralInvariantsHealthy
              ? 'Schema financeiro incompatível'
              : overview.data.unbalanced > 0
                ? `${overview.data.unbalanced} ${pluralize(
                    overview.data.unbalanced,
                    'transação desbalanceada detectada',
                    'transações desbalanceadas detectadas',
                  )}`
                : 'Não foi possível confirmar a integridade contábil'
          }
          description={
            !structuralInvariantsHealthy
              ? 'O motor divergiu dos invariantes do hard cutover. Interrompa a operação, confira o status das migrations e siga o runbook contábil antes de continuar.'
              : overview.data.unbalanced > 0
                ? `Há ${overview.data.unbalanced} ${pluralize(
                    overview.data.unbalanced,
                    'transação desbalanceada',
                    'transações desbalanceadas',
                  )} no diário. As leituras foram bloqueadas até que a divergência seja corrigida e a integridade seja novamente confirmada.`
                : 'A atualização dos invariantes falhou. As leituras foram bloqueadas, inclusive os dados em cache, até uma nova validação íntegra.'
          }
        />
      ) : null}
      {accountingOverview ? (
        <div className={statGrid} aria-label="Resumo contábil">
          <Metric
            label="Transações"
            value={accountingOverview.transactions}
            hint="Fatos financeiros registrados"
            icon={<BookCheck size={17} />}
          />
          <Metric
            label="Lançamentos"
            value={accountingOverview.postings}
            hint="Débitos e créditos imutáveis"
            icon={<ListChecks size={17} />}
          />
          <Metric
            label="Contas"
            value={accountingOverview.accounts}
            hint="Contas com rastreabilidade"
            icon={<Landmark size={17} />}
          />
          <Metric
            label="Lotes de recebíveis"
            value={accountingOverview.receivableLots}
            hint="Lotes financeiros imutáveis"
            icon={<Layers3 size={17} />}
          />
          <Metric
            label="Eventos do subledger"
            value={accountingOverview.receivableEvents}
            hint="Transições append-only dos lotes"
            icon={<History size={17} />}
          />
          <Metric
            label="Desbalanceadas"
            value={accountingOverview.unbalanced}
            hint={
              accountingOverview.unbalanced > 0
                ? 'Revisão imediata necessária'
                : 'Nenhuma divergência encontrada'
            }
            icon={<Scale size={17} />}
            critical={accountingOverview.unbalanced > 0}
          />
        </div>
      ) : null}

      {accountingReady ? (
        <Card padding="md" className={accountingToolbar}>
          <div className={accountingToolbarHeader}>
            <div className={accountingViewTabs}>
              <SegmentedControl
                ariaLabel="Visão contábil"
                value={view}
                options={viewOptions}
                onChange={handleViewChange}
              />
            </div>
            <span className={muted} aria-live="polite">
              {view === 'transactions'
                ? visibleItemsLabel(transactionItems.length, 'transação', 'transações')
                : visibleItemsLabel(trialBalanceItems.length, 'conta', 'contas')}
            </span>
          </div>
          <FilterToolbar
            chips={
              hasActiveFilters ? (
                <>
                  {query ? <Chip label={`Busca: ${query}`} onRemove={() => setQuery('')} /> : null}
                  {sourceType ? (
                    <Chip label={sourceLabel(sourceType)} onRemove={() => setSourceType('')} />
                  ) : null}
                </>
              ) : null
            }
            onClear={hasActiveFilters ? clearFilters : undefined}
          >
            <div className={accountingFilterGrid}>
              {view === 'transactions' ? (
                <>
                  <div className={accountingFilterSearch}>
                    <span className={fieldLabel}>Busca</span>
                    <TextField
                      type="search"
                      aria-label="Buscar transações contábeis"
                      value={query}
                      onChange={(event) => setQuery(event.currentTarget.value)}
                      placeholder="ID, evento ou origem"
                      leftIcon={<Search size={16} aria-hidden />}
                    />
                  </div>
                  <SelectField
                    label="Tipo do fato"
                    value={sourceType}
                    onChange={(event) => setSourceType(event.currentTarget.value)}
                    options={sourceOptions}
                  />
                </>
              ) : null}
            </div>
          </FilterToolbar>
        </Card>
      ) : null}

      {accountingReady && view === 'transactions' ? (
        <TransactionsTable
          items={transactionItems}
          loading={transactions.isLoading}
          error={transactions.isError}
          hasMore={transactions.hasNextPage}
          loadingMore={transactions.isFetchingNextPage}
          loadMoreError={transactions.isFetchNextPageError}
          hasActiveFilters={hasActiveFilters}
          onLoadMore={() => void transactions.fetchNextPage()}
          onClearFilters={clearFilters}
          onSelect={handleTransactionSelect}
        />
      ) : accountingReady ? (
        <TrialBalanceTable
          items={trialBalanceItems}
          loading={trialBalance.isLoading}
          error={trialBalance.isError}
          hasMore={trialBalance.hasNextPage}
          loadingMore={trialBalance.isFetchingNextPage}
          loadMoreError={trialBalance.isFetchNextPageError}
          hasActiveFilters={hasActiveFilters}
          onLoadMore={() => void trialBalance.fetchNextPage()}
          onClearFilters={clearFilters}
        />
      ) : null}

      {accountingReady ? (
        <AccountingDrawer transactionId={selectedId} onClose={handleDrawerClose} />
      ) : null}
    </section>
  )
}

function visibleItemsLabel(count: number, singular: string, plural: string): string {
  return `${count} ${count === 1 ? singular : plural} ${count === 1 ? 'exibida' : 'exibidas'}`
}

function pluralize(count: number, singular: string, plural: string): string {
  return count === 1 ? singular : plural
}

function Metric({
  label,
  value,
  hint,
  icon,
  critical = false,
}: {
  label: string
  value: number
  hint: string
  icon: React.ReactNode
  critical?: boolean
}) {
  return (
    <Card padding="md" className={metricCard}>
      <div className={metricHeader}>
        <span className={metricLabel}>{label}</span>
        <span className={metricIcon}>{icon}</span>
      </div>
      <span className={kpiValue}>{value}</span>
      <span className={accountingMetricHint}>{hint}</span>
      {critical ? <StatusBadge label="Requer ação" tone="closed" size="sm" /> : null}
    </Card>
  )
}

function TransactionsTable({
  items,
  loading,
  error,
  hasMore,
  loadingMore,
  loadMoreError,
  hasActiveFilters,
  onLoadMore,
  onClearFilters,
  onSelect,
}: {
  items: AccountingTransaction[]
  loading: boolean
  error: boolean
  hasMore?: boolean
  loadingMore: boolean
  loadMoreError: boolean
  hasActiveFilters: boolean
  onLoadMore: () => void
  onClearFilters: () => void
  onSelect: (id: string) => void
}) {
  if (loading) return <AccountingTableSkeleton />
  if (error && items.length === 0)
    return <ErrorState title="Não foi possível carregar as transações." />
  if (items.length === 0) {
    return (
      <EmptyState
        icon={<BookCheck size={22} />}
        title={hasActiveFilters ? 'Nenhuma transação encontrada' : 'Nenhuma transação contábil'}
        description={
          hasActiveFilters
            ? 'Revise os filtros para ampliar o resultado da busca.'
            : 'Processe um novo fato financeiro para iniciar o diário imutável.'
        }
        action={
          hasActiveFilters ? (
            <Button variant="secondary" size="sm" onClick={onClearFilters}>
              Limpar filtros
            </Button>
          ) : undefined
        }
      />
    )
  }
  return (
    <Card padding="none" className={tableCard}>
      <div className={tableViewport}>
        <table className={`${table} ${accountingTable}`}>
          <thead className={accountingTableHead}>
            <tr>
              <th className={th}>Fato</th>
              <th className={th}>Origem</th>
              <th className={th}>Débitos</th>
              <th className={th}>Créditos</th>
              <th className={th}>Data econômica</th>
              <th className={th}>Balanceamento</th>
            </tr>
          </thead>
          <tbody className={accountingTableBody}>
            {items.map((item) => (
              <tr
                key={item.id}
                className={`${clickableRow} ${accountingTableRow}`}
                tabIndex={0}
                aria-label={`Abrir ${sourceLabel(item.sourceType)}`}
                onClick={() => onSelect(item.id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') onSelect(item.id)
                }}
              >
                <td
                  className={`${td} ${accountingCell} ${accountingPrimaryCell}`}
                  data-label="Fato"
                >
                  <span className={accountingCellValue}>
                    <strong>{sourceLabel(item.sourceType)}</strong>
                    <span className={muted}>{item.postingsCount} lançamentos</span>
                  </span>
                </td>
                <td className={`${td} ${accountingCell}`} data-label="Origem">
                  <TechnicalId label="Origem" value={item.sourceId} />
                </td>
                <td
                  className={`${td} ${accountingCell} ${accountingAmountCell}`}
                  data-label="Débitos"
                >
                  <MoneyValue
                    amount={item.debits.amount}
                    currency={item.currency}
                    fractionDigits={3}
                  />
                </td>
                <td
                  className={`${td} ${accountingCell} ${accountingAmountCell}`}
                  data-label="Créditos"
                >
                  <MoneyValue
                    amount={item.credits.amount}
                    currency={item.currency}
                    fractionDigits={3}
                  />
                </td>
                <td className={`${td} ${accountingCell}`} data-label="Data econômica">
                  {formatDateTime(item.effectiveAt)}
                </td>
                <td className={`${td} ${accountingCell}`} data-label="Balanceamento">
                  <StatusBadge
                    label={item.balanced ? 'Fecha em zero' : 'Divergente'}
                    tone={item.balanced ? 'success' : 'closed'}
                    size="sm"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <LoadMore
        shown={items.length}
        hasMore={hasMore === true}
        loading={loadingMore}
        onClick={onLoadMore}
        error={loadMoreError}
        errorMessage="Não foi possível carregar mais transações."
      />
    </Card>
  )
}

function TrialBalanceTable({
  items,
  loading,
  error,
  hasMore,
  loadingMore,
  loadMoreError,
  hasActiveFilters,
  onLoadMore,
  onClearFilters,
}: {
  items: AccountingBalance[]
  loading: boolean
  error: boolean
  hasMore?: boolean
  loadingMore: boolean
  loadMoreError: boolean
  hasActiveFilters: boolean
  onLoadMore: () => void
  onClearFilters: () => void
}) {
  if (loading) return <AccountingTableSkeleton />
  if (error && items.length === 0)
    return <ErrorState title="Não foi possível carregar o balancete." />
  if (!Array.isArray(items) || items.length === 0) {
    return (
      <EmptyState
        icon={<Scale size={22} />}
        title={hasActiveFilters ? 'Nenhuma conta encontrada' : 'Balancete vazio'}
        description={
          hasActiveFilters
            ? 'Não há contas para a moeda informada.'
            : 'Nenhuma conta recebeu lançamentos.'
        }
        action={
          hasActiveFilters ? (
            <Button variant="secondary" size="sm" onClick={onClearFilters}>
              Limpar filtros
            </Button>
          ) : undefined
        }
      />
    )
  }
  return (
    <Card padding="none" className={tableCard}>
      <div className={tableViewport}>
        <table className={`${table} ${accountingTable}`}>
          <thead className={accountingTableHead}>
            <tr>
              <th className={th}>Conta</th>
              <th className={th}>Categoria</th>
              <th className={th}>Natureza</th>
              <th className={th}>Dono</th>
              <th className={th}>Saldo</th>
            </tr>
          </thead>
          <tbody className={accountingTableBody}>
            {items.map((item) => (
              <tr key={item.accountId} className={accountingTableRow}>
                <td
                  className={`${td} ${accountingCell} ${accountingPrimaryCell}`}
                  data-label="Conta"
                >
                  <span className={accountingCellValue}>
                    <strong>{item.name}</strong>
                    <span className={muted}>{item.code}</span>
                  </span>
                </td>
                <td className={`${td} ${accountingCell}`} data-label="Categoria">
                  {accountingCategoryLabel(item.category)}
                </td>
                <td className={`${td} ${accountingCell}`} data-label="Natureza">
                  {normalSideLabel(item.normalSide)}
                </td>
                <td className={`${td} ${accountingCell}`} data-label="Dono">
                  {item.ownerId ?? 'VendePay'}
                </td>
                <td
                  className={`${td} ${accountingCell} ${accountingAmountCell}`}
                  data-label="Saldo"
                >
                  <MoneyValue
                    amount={item.balance.amount}
                    currency={item.currency}
                    fractionDigits={3}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <LoadMore
        shown={items.length}
        hasMore={hasMore === true}
        loading={loadingMore}
        onClick={onLoadMore}
        error={loadMoreError}
        errorMessage="Não foi possível carregar mais contas do balancete."
      />
    </Card>
  )
}

function AccountingTableSkeleton() {
  return (
    <Card padding="md" className={accountingSkeleton} aria-label="Carregando dados contábeis">
      {[0, 1, 2, 3].map((row) => (
        <div key={row} className={accountingSkeletonRow}>
          <Skeleton width="34%" height={14} />
          <Skeleton width="20%" height={14} />
          <Skeleton width="16%" height={14} />
        </div>
      ))}
    </Card>
  )
}

function AccountingDrawer({
  transactionId,
  onClose,
}: {
  transactionId: string | null
  onClose: () => void
}) {
  const query = useAccountingTransaction(transactionId)
  const transaction = query.data?.transaction ?? null
  return (
    <Drawer
      open={transactionId !== null}
      onClose={onClose}
      title={transaction ? sourceLabel(transaction.sourceType) : 'Transação contábil'}
      description={
        transaction
          ? `${transaction.eventType} · ${formatDateTime(transaction.effectiveAt)}`
          : 'Partidas, origem e rastreabilidade desta movimentação.'
      }
      size="audit"
      variant="financial"
      status={
        transaction ? (
          <div className={ledgerStatusGroup}>
            <StatusBadge
              label={transaction.balanced ? 'Balanceada' : 'Desbalanceada'}
              tone={transaction.balanced ? 'success' : 'closed'}
            />
            <StatusBadge
              label={transaction.integrityValid ? 'Hash válido' : 'Hash divergente'}
              tone={transaction.integrityValid ? 'success' : 'closed'}
            />
          </div>
        ) : undefined
      }
    >
      {query.isLoading ? <Skeleton height={360} /> : null}
      {query.isError ? <ErrorState title="Não foi possível carregar o detalhe." /> : null}
      {transaction ? (
        <div className={ledgerDetail}>
          <DetailHero
            eyebrow="Fato contábil"
            value={sourceLabel(transaction.sourceType)}
            description={transaction.eventType}
            aside={formatDateTime(transaction.recordedAt)}
          />
          <div className={ledgerDetailRows}>
            <Detail label="Data econômica" value={formatDateTime(transaction.effectiveAt)} />
            <Detail label="Data de registro" value={formatDateTime(transaction.recordedAt)} />
            {transaction.settledAt ? (
              <Detail label="Data de liquidação" value={formatDateTime(transaction.settledAt)} />
            ) : null}
            <Detail label="Produtor" value={transaction.createdBy} />
            {transaction.reason ? <Detail label="Motivo" value={transaction.reason} /> : null}
          </div>
          <TechnicalDetails
            rows={[
              { label: 'Transação', value: transaction.id },
              { label: 'Origem', value: transaction.sourceId },
              { label: 'Evento', value: transaction.eventId },
              { label: 'Correlação', value: transaction.correlationId },
              ...(transaction.causationId
                ? [{ label: 'Causalidade', value: transaction.causationId }]
                : []),
              { label: 'Hash SHA-256', value: transaction.integrityHash },
              ...(transaction.reversalOf
                ? [{ label: 'Reversão de', value: transaction.reversalOf }]
                : []),
            ]}
          />
          {Object.keys(transaction.metadata).length > 0 ? (
            <div className={ledgerTechnicalRefs}>
              <span className={fieldLabel}>Metadados do fato</span>
              <pre className={jsonBox}>{JSON.stringify(transaction.metadata, null, 2)}</pre>
            </div>
          ) : null}
          <PostingsTable postings={transaction.postings} />
        </div>
      ) : null}
    </Drawer>
  )
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div className={ledgerDetailRow}>
      <span className={ledgerDetailLabel}>{label}</span>
      <span className={ledgerDetailValue}>{value}</span>
    </div>
  )
}

function PostingsTable({ postings }: { postings: AccountingPosting[] }) {
  return (
    <>
      <span className={fieldLabel}>Partidas</span>
      <Card padding="none" className={tableCard}>
        <div className={tableViewport}>
          <table className={`${table} ${accountingPostingsTable}`}>
            <thead className={accountingPostingsHead}>
              <tr>
                <th className={`${th} ${accountingPostingCell} ${accountingPostingSideCell}`}>
                  Lado
                </th>
                <th className={`${th} ${accountingPostingCell}`}>Conta</th>
                <th className={`${th} ${accountingPostingCell} ${accountingPostingValueCell}`}>
                  Valor
                </th>
                <th className={`${th} ${accountingPostingCell} ${accountingPostingRefsCell}`}>
                  Rastreabilidade
                </th>
              </tr>
            </thead>
            <tbody className={accountingPostingsBody}>
              {postings.map((posting) => (
                <tr key={posting.id} className={accountingPostingRow}>
                  <td className={`${td} ${accountingPostingCell} ${accountingPostingSideCell}`}>
                    <StatusBadge
                      label={posting.side === 'DEBIT' ? 'Débito' : 'Crédito'}
                      tone={posting.side === 'DEBIT' ? 'informative' : 'success'}
                      size="sm"
                    />
                  </td>
                  <td className={`${td} ${accountingPostingCell}`}>
                    <strong>{posting.accountName}</strong>
                    <br />
                    <span className={muted}>{posting.accountCode}</span>
                  </td>
                  <td className={`${td} ${accountingPostingCell} ${accountingPostingValueCell}`}>
                    <MoneyValue
                      amount={posting.amount.amount}
                      currency={posting.amount.currency}
                      fractionDigits={3}
                    />
                  </td>
                  <td className={`${td} ${accountingPostingCell} ${accountingPostingRefsCell}`}>
                    {postingReferences(posting).map(([label, value]) => (
                      <TechnicalId key={label} label={label} value={value} />
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  )
}

function sourceLabel(sourceType: string): string {
  const labels: Record<string, string> = {
    SALE_ALLOCATION: 'Venda alocada',
    SALE_REVERSAL: 'Venda revertida',
    DEBT_CREATED: 'Dívida criada',
    DEBT_SETTLEMENT: 'Dívida liquidada',
    DISPUTE_DEBT_REVERSED: 'Dívida revertida por disputa ganha',
    WITHDRAWAL_CONSUMED: 'Saldo reservado para recebimento',
    WITHDRAWAL_COMPLETED: 'Recebimento concluído',
    WITHDRAWAL_COMPLETED_SOURCE: 'Conversão do recebimento na origem',
    WITHDRAWAL_COMPLETED_DESTINATION: 'Conversão do recebimento no destino',
    WITHDRAWAL_REVERTED: 'Recebimento revertido',
  }
  if (labels[sourceType]) return labels[sourceType]
  return sourceType
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

function postingReferences(posting: AccountingPosting): Array<[string, string]> {
  return [
    ['Participante', posting.participantId],
    ['Venda', posting.saleId],
    ['Recebimento', posting.withdrawalId],
    ['Dívida', posting.debtId],
    ['Lote de recebível', posting.sourceEntryId],
    ['Linha', posting.lineId],
  ].filter((item): item is [string, string] => item[1] !== undefined)
}

function accountingCategoryLabel(category: string): string {
  const labels: Record<string, string> = {
    ASSET: 'Ativo',
    LIABILITY: 'Passivo',
    REVENUE: 'Receita',
    EXPENSE: 'Despesa',
    EQUITY: 'Patrimônio líquido',
    CLEARING: 'Compensação',
  }
  return labels[category] ?? category
}

function normalSideLabel(normalSide: AccountingBalance['normalSide']): string {
  return normalSide === 'DEBIT' ? 'Devedora' : 'Credora'
}

function formatDateTime(value: string): string {
  return new Date(value).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}
